<template>
    <div
        class="grid grid-cols-12 base-responsive-gap"
        :class="{ flipX: flipX }"
        style="grid-template-rows: auto 1fr"
        role="region"
        :aria-label="$t('a11y.gallery')"
    >
        <div
            class="col-span-12 separator-border-top-line"
            :class="
                flipX
                    ? 'order-1 lg:order-2 lg:row-start-1 lg:col-span-5 xl:col-span-5'
                    : 'order-1 lg:row-start-1 lg:col-span-5 xl:col-span-5'
            "
        >
            <h2 :class="featured ? 'header-1' : 'header-2'"><T :t="titleKey" /></h2>
        </div>
        <div
            class="col-span-12"
            :class="
                flipX
                    ? 'lg:col-span-5 lg:col-start-8 xl:col-span-5 order-3 lg:row-start-2'
                    : 'lg:col-span-5 lg:col-start-1 xl:col-span-5 order-3 sm:order-2 lg:row-start-2'
            "
        >
            <div class="whitespace-pre-line" :class="{ lead: featured }"><T :t="descriptionKey" /></div>
            <div v-if="detailsDescriptionKey" class="whitespace-pre-line mt-4"><T :t="detailsDescriptionKey" /></div>
        </div>

        <div
            class="col-span-12 mt-4 lg:mt-0 relative"
            :class="
                flipX
                    ? 'lg:col-span-7 lg:col-start-1 sm:row-span-2 lg:row-start-1 order-2 sm:order-1'
                    : 'lg:col-span-7 lg:col-start-6 sm:row-span-2 lg:row-start-1 order-2 sm:order-3'
            "
        >
            <div class="swiper-container">
                <div ref="swiper" class="swiper mx-[-10px] px-[10px]">
                    <ul class="swiper-wrapper sm:mx-0 sm:px-0 sm:grid sm:grid-cols-7 sm:gap-7">
                        <li v-for="(image, i) in images" :key="`gallery-image-${i}`" class="swiper-slide block">
                            <div @click="() => openLightbox(i)">
                                <div class="swiper-slide__inner base-responsive-rounding cursor-pointer">
                                    <img
                                        :src="image.src"
                                        :alt="`${$t('generic.gallery_image_alt_text')} ${i}`"
                                        class="object-cover w-full h-full"
                                    />
                                </div>
                            </div>
                        </li>
                    </ul>

                    <div v-if="false" class="text-right mt-2 py-2">
                        <Button
                            class="p-button-secondary p-button-text"
                            :label="$t('a11y.open_gallery')"
                            @click.native="() => openLightbox()"
                        />
                    </div>

                    <div
                        class="carousel-pagination-block container relative mt-6 grid grid-cols-12 base-responsive-gap sm:hidden"
                    >
                        <nav
                            :aria-label="$t('a11y.gallery_stepper')"
                            class="col-span-12 flex items-center justify-center"
                        >
                            <button ref="prevBtn" class="swiper-button-prev"></button>
                            <span class="gallery-pagination-counter">01</span>
                            <div ref="pagination" class="swiper-pagination"></div>
                            <span class="gallery-pagination-counter">
                                {{ String(images.length).padStart(2, '0') }}
                            </span>
                            <button ref="nextBtn" class="swiper-button-next"></button>
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Type6Block',
    props: {
        featured: {
            type: Boolean,
            required: false,
            default: false,
        },
        titleKey: {
            type: String,
            required: true,
        },
        descriptionKey: {
            type: String,
            required: true,
        },
        detailsDescriptionKey: {
            type: String,
            required: false,
            default: null,
        },
        images: {
            type: Array,
            required: true,
        },
        flipX: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isLightboxOpen: false,
        };
    },
    mounted() {
        this.gallery = new this.$swiper(this.$refs.swiper, {
            spaceBetween: 16,
            slidesPerView: 1.2,
            centeredSlides: false,
            loop: false,
            navigation: {
                nextEl: this.$refs.prevBtn,
                prevEl: this.$refs.nextBtn,
            },
            pagination: {
                el: this.$refs.pagination,
                clickable: true,
                bulletElement: 'button',
            },
            breakpoints: {
                640: {
                    a11y: {
                        enabled: false,
                    },
                },
            },
            a11y: {
                containerMessage: this.$t('carousel.a11y.container_message_gallery'),
                paginationBulletMessage: this.$t('carousel.a11y.pagination_bullet_message'),
                firstSlideMessage: this.$t('carousel.a11y.first_slide_message'),
                lastSlideMessage: this.$t('carousel.a11y.last_slide_message'),
                nextSlideMessage: this.$t('carousel.a11y.next_slide_message'),
                prevSlideMessage: this.$t('carousel.a11y.prev_slide_message'),
            },
        });
    },
    beforeDestroy() {
        this.gallery.destroy();
    },
    methods: {
        openLightbox(slideIndex) {
            this.$nuxt.$emit('open-lightbox', {
                index: slideIndex,
                items: this.images,
            });
        },
    },
};
</script>

<style scoped>
@media (max-width: 639px) {
    .swiper-slide__inner {
        @apply aspect-w-1 aspect-h-1;
    }
}

@media (min-width: 640px) {
    .swiper-slide {
        width: unset !important;
        margin: unset !important;

        &:nth-child(1) {
            @apply col-span-6 col-start-2;
            .swiper-slide__inner {
                @apply aspect-w-3 aspect-h-2;
            }
        }
        &:nth-child(2) {
            @apply col-span-4;
            .swiper-slide__inner {
                @apply aspect-w-3 aspect-h-2;
            }
        }
        &:nth-child(3) {
            @apply col-span-3;
            .swiper-slide__inner {
                @apply aspect-w-9 aspect-h-10;
            }
        }
    }

    .flipX .swiper-slide:nth-child(1) {
        @apply col-start-1 lg:col-start-2;
    }
}
</style>
